import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { Schema } from '../../../../amplify/data/resource';
import Button from '../../../components/custom/Button';
import Tabs from '../../../components/custom/Tabs';
import TextField from '../../../components/custom/TextField';
import { formatIsraeliPhoneNumber } from '../../../utils/format/format';

const apiClient = generateClient<Schema>();

interface FormValues {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  bank: string;
  branch: string;
  account: string;
  message: string;
  from: string;
  distributions: {
    fundNumber: string;
    amount: number;
    description: string;
  }[];
}

const Integrated = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      distributions: []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "distributions"
  });

  const onSubmit = async (formData: FormValues) => {
    try {
      setLoading(true);

      const createPayerResponse = await apiClient.queries.oxsCreatePayer({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email,
      });

      if (!createPayerResponse?.data) {
        throw new Error('Failed to create payer');
      }

      const parsedPayerData = typeof createPayerResponse.data === 'string'
        ? JSON.parse(createPayerResponse.data)
        : createPayerResponse.data;

      if (parsedPayerData.statusCode !== 200) {
        throw new Error('Failed to create payer');
      }

      const payerId = parsedPayerData.payerId;

      const distributionsWithFloatAmounts = formData.distributions.map(dist => ({
        fundNumber: dist.fundNumber,
        amount: parseFloat(dist.amount.toString()),
        description: dist.description
      }));

      const paymentResponse = await apiClient.queries.oxsPayment({
        bank: formData.bank,
        branch: formData.branch,
        account: formData.account,
        message: formData.message,
        from: formData.from,
        distributions: JSON.stringify(distributionsWithFloatAmounts),
        payerId: payerId,
        fileId: '',
      });

      if (!paymentResponse?.data) {
        throw new Error('Failed to create payment');
      }

      const parsedPaymentData = typeof paymentResponse.data === 'string'
        ? JSON.parse(paymentResponse.data)
        : paymentResponse.data;

      if (parsedPaymentData.statusCode === 200) {
        enqueueSnackbar('התשלום נוצר בהצלחה', { variant: 'success' });
      } else {
        enqueueSnackbar('אירעה תקלה', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('אירעה תקלה', { variant: 'error' });
      console.error('Failed to submit:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack
      component="form"
      spacing={2}
      width={'400px'}
      alignItems={'flex-end'}
      sx={{ ml: 'auto', direction: 'rtl' }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Typography sx={{ width: '100%' }} fontSize="18px" fontWeight="700" fontFamily={'AlmoniDL'} mb={2} textAlign="right">O</Typography>

      <Controller
        name="firstName"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="שם פרטי"
            fullWidth
            required
            error={!!errors.firstName}
            value={field.value?.toString() || ''}
            helperText={errors.firstName?.message}
          />
        )}
      />
      <Controller
        name="lastName"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="שם משפחה"
            fullWidth
            required
            error={!!errors.lastName}
            value={field.value?.toString() || ''}
            helperText={errors.lastName?.message}
          />
        )}
      />
      <Controller
        name="phone"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="טלפון"
            fullWidth
            required
            error={!!errors.phone}
            value={field.value?.toString() || ''}
            helperText={errors.phone?.message}
          />
        )}
      />
      <Controller
        name="email"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="דואר אלקטרוני"
            fullWidth
            required
            error={!!errors.email}
            value={field.value?.toString() || ''}
            helperText={errors.email?.message}
          />
        )}
      />
      <Divider
        sx={{
          width: '100%',
          height: '1px',
          backgroundColor: theme.palette.dark.border,
        }}
      />
      <Controller
        name="bank"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="בנק"
            fullWidth
            required
            error={!!errors.bank}
            value={field.value?.toString() || ''}
            helperText={errors.bank?.message}
          />
        )}
      />
      <Controller
        name="branch"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="סניף"
            fullWidth
            required
            error={!!errors.branch}
            value={field.value?.toString() || ''}
            helperText={errors.branch?.message}
          />
        )}
      />
      <Controller
        name="account"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="מספר בנק"
            fullWidth
            error={!!errors.account}
            value={field.value?.toString() || ''}
            helperText={errors.account?.message}
          />
        )}
      />
      <Controller
        name="from"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="מאת"
            fullWidth
            required
            error={!!errors.from}
            value={field.value?.toString() || ''}
            helperText={errors.from?.message}
          />
        )}
      />
      <Controller
        name="message"
        control={control}
        rules={{ required: 'חובה למלא את השדה' }}
        render={({ field }) => (
          <TextField
            {...field}
            label="הודעה"
            fullWidth
            required
            error={!!errors.message}
            value={field.value?.toString() || ''}
            helperText={errors.message?.message}
          />
        )}
      />

      <Divider
        sx={{
          width: '100%',
          height: '1px',
          backgroundColor: theme.palette.dark.border,
        }}
      />

      <Box width="100%">
        <Typography fontSize="18px" fontWeight="700" color="black" fontFamily={'AlmoniDL'} mb={2} textAlign="right">Distributions</Typography>
        {fields.map((field, index) => (
          <Box key={field.id} mb={2} py={2}>
            <Stack spacing={2} alignItems="flex-end">
              <Controller
                name={`distributions.${index}.fundNumber`}
                control={control}
                rules={{ required: 'חובה למלא את השדה' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="מספר קרן"
                    fullWidth
                    required
                    error={!!errors.distributions?.[index]?.fundNumber}
                    helperText={errors.distributions?.[index]?.fundNumber?.message}
                    value={field.value?.toString() || ''}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                )}
              />
              <Controller
                name={`distributions.${index}.amount`}
                control={control}
                rules={{ required: 'חובה למלא את השדה' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="סכום"
                    fullWidth
                    required
                    error={!!errors.distributions?.[index]?.amount}
                    helperText={errors.distributions?.[index]?.amount?.message}
                    value={field.value?.toString() || ''}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                )}
              />
              <Controller
                name={`distributions.${index}.description`}
                control={control}
                rules={{ required: 'חובה למלא את השדה' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="תיאור"
                    fullWidth
                    required
                    error={!!errors.distributions?.[index]?.description}
                    helperText={errors.distributions?.[index]?.description?.message}
                    value={field.value?.toString() || ''}
                    inputProps={{ style: { textAlign: 'right' } }}
                  />
                )}
              />
              <Button
                onClick={() => remove(index)}
                sx={{
                  backgroundColor: '#ff4444',
                  color: 'white',
                  ':hover': { backgroundColor: '#cc0000' },
                }}
              >
                הסר
              </Button>
            </Stack>
          </Box>
        ))}
        <Button
          onClick={() => append({ fundNumber: '', amount: 0, description: '' })}
          sx={{
            backgroundColor: '#4CAF50',
            color: 'white',
            ':hover': { backgroundColor: '#45a049' },
            width: '100%',
            mb: 2
          }}
        >
          הוסף
        </Button>
      </Box>

      <Button
        type="submit"
        loading={loading}
        sx={{
          backgroundColor: '#B6F829',
          borderRadius: '15px',
          ':hover': { backgroundColor: '#B6F829' },
          width: '100%',
        }}
      >
        <Typography
          fontSize="18px"
          fontWeight="700"
          color="black"
          fontFamily={'AlmoniDL'}
        >
          שליחת לינק
        </Typography>
      </Button>
    </Stack>
  );
};

export default Integrated;